import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, X, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { format } from 'date-fns';
import { useFilters } from '../contexts/FilterContext';
import { equipmentAPI } from '../services/api';
import { DatePicker, ConfigProvider } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

// Helper to format dates for API
const formatDateForApi = (date) => {
  if (!(date instanceof Date)) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const FilterBar = ({ activeTab = 'Productivity' }) => {
  const { updateFilters, triggerManualFetch, manualTrigger, availableMachines } = useFilters();

  // Initialize date range to this month start date to today - shows this month's data on login
  const getInitialDateRange = () => {
    const today = new Date();
    const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: thisMonthStart, to: today };
  };

  const [dateRange, setDateRange] = useState(getInitialDateRange);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    // Initialize calendar month to show the selected date range
    const today = new Date();
    const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    return thisMonthStart;
  });
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // New state for locations, machines, and shifts
  const [locations, setLocations] = useState([]);
  const [machines, setMachines] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedMachine, setSelectedMachine] = useState('');
  const [selectedShift, setSelectedShift] = useState('');
  const [selectedEquipmentType, setSelectedEquipmentType] = useState('1');
  const [selectedLocationName, setSelectedLocationName] = useState('');
  const [selectedMachineName, setSelectedMachineName] = useState('');

  // Refs to prevent duplicate API calls
  const isFetchingEquipmentRef = useRef(false);
  const lastEquipmentFetchKeyRef = useRef('');
  const equipmentInitialLoadRef = useRef(false);
  const locationAutoSelectedRef = useRef(false);

  // Update calendar month when date range changes
  useEffect(() => {
    if (dateRange?.from) {
      // Set calendar month to show the start of the selected date range
      const monthToShow = new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), 1);
      setCalendarMonth(monthToShow);
    }
  }, [dateRange?.from?.getTime()]);

  // Update context whenever filters change
  useEffect(() => {
    updateFilters({
      dateRange,
      selectedCompany,
      selectedLocation,
      selectedLocationName,
      selectedMachine: selectedMachine === 'all' ? '' : selectedMachine,
      selectedMachineName,
      selectedEquipmentType: selectedEquipmentType === 'all' ? '' : selectedEquipmentType,
      selectedShift: selectedShift === 'all' ? '' : selectedShift
    });
  }, [
    updateFilters,
    dateRange?.from?.getTime(),
    dateRange?.to?.getTime(),
    selectedCompany,
    selectedLocation,
    selectedLocationName,
    selectedMachine,
    selectedMachineName,
    selectedEquipmentType,
    selectedShift
  ]);

  useEffect(() => {
    // Fetch companies from stored user data (set during login)
    const fetchStoredCompanies = () => {
      try {
        setLoading(true);

        // Get stored companies from localStorage or sessionStorage
        const storedCompaniesStr = localStorage.getItem('userCompanies') || sessionStorage.getItem('userCompanies');
        const storedDefaultCompanyId = localStorage.getItem('companyId') || sessionStorage.getItem('companyId');

        console.log('🔄 Loading companies from storage...');

        if (storedCompaniesStr) {
          const parsedCompanies = JSON.parse(storedCompaniesStr);
          console.log('✅ Found stored companies:', parsedCompanies);

          if (Array.isArray(parsedCompanies) && parsedCompanies.length > 0) {
            // Map to component format
            const mappedCompanies = parsedCompanies.map(c => ({
              companyId: c.id,
              companyName: c.company_name,
              companyShortName: c.company_name // Use name as shortname since not in API response
            }));

            setCompanies(mappedCompanies);

            // Set selected company - try default from storage, otherwise first in list
            if (storedDefaultCompanyId && mappedCompanies.find(c => c.companyId.toString() === storedDefaultCompanyId.toString())) {
              setSelectedCompany(storedDefaultCompanyId.toString());
              console.log(`✨ Default company set from storage: ${storedDefaultCompanyId}`);
            } else {
              setSelectedCompany(mappedCompanies[0].companyId.toString());
              console.log(`✨ Default company set to first available: ${mappedCompanies[0].companyName}`);
            }
          } else {
            // No valid companies in API response
            console.warn('⚠️ No valid companies found in API response. Companies dropdown will be empty.');
            setCompanies([]);
            setSelectedCompany('');
          }
        } else {
          // No stored companies from API
          console.warn('⚠️ No stored companies found from API. Companies dropdown will be empty.');
          setCompanies([]);
          setSelectedCompany('');
        }

      } catch (error) {
        console.error('❌ Error loading companies from storage:', error);
        setCompanies([]);
      } finally {
        setLoading(false);
      }
    };

    fetchStoredCompanies();
  }, []);

  // Reset equipment initial load ref when company changes
  useEffect(() => {
    equipmentInitialLoadRef.current = false;
    lastEquipmentFetchKeyRef.current = ''; // Reset fetch key cache when company changes
    locationAutoSelectedRef.current = false; // Reset location auto-selection when company changes
  }, [selectedCompany]);

  // Fetch filter options (locations, machines, shifts) when company or date range changes
  useEffect(() => {
    const fetchFilterOptions = async () => {
      if (!selectedCompany || !dateRange?.from || !dateRange?.to) {
        return;
      }

      try {
        console.log('🔄 Fetching filter options (locations, machines, shifts)...');
        // NOTE: We no longer call GetProductivitySummary API here.
        // Locations, machines, and shifts are loaded from user's login data stored in localStorage/sessionStorage.

        console.log('📍 Loading locations from user data...');

        // Get locations from stored user data (from login response)
        const storedLocations = localStorage.getItem('locationIds') || sessionStorage.getItem('locationIds');
        let userLocations = [];

        if (storedLocations) {
          try {
            // Parse stored locations: [{"id": "223", "location": "HOCP"}, {"id": "224", "location": "GOCP"}]
            const parsedLocations = JSON.parse(storedLocations);
            userLocations = parsedLocations.map(loc => ({
              locationId: loc.id,
              locationName: loc.location
            }));
            console.log('✅ Loaded locations from user data:', userLocations);
          } catch (e) {
            console.warn('⚠️ Failed to parse stored locations:', e);
          }
        }

        // Only use locations from API
        setLocations(userLocations);

        // Auto-select first location if available (only on initial load)
        if (userLocations.length > 0 && !locationAutoSelectedRef.current) {
          const firstLocation = userLocations[0];
          setSelectedLocation(firstLocation.locationId.toString());
          setSelectedLocationName(firstLocation.locationName);
          locationAutoSelectedRef.current = true;
          console.log('✨ Auto-selected first location:', firstLocation.locationName);
        }

        // Fetch equipment list
        // For Spares tab: use dedicated equipment API endpoint
        // For other tabs (Productivity, Fuel, Maintenance): use productivity analytics API
        if (activeTab === 'Spares') {
          // Fetch equipment from dedicated equipment API for Spares tab
          const fetchKey = `${selectedCompany}-spares-equipment`;

          const isInitialLoad = !equipmentInitialLoadRef.current && selectedCompany;
          const parametersChanged = fetchKey !== lastEquipmentFetchKeyRef.current && selectedCompany;

          if (!isFetchingEquipmentRef.current && (isInitialLoad || parametersChanged) && fetchKey !== lastEquipmentFetchKeyRef.current) {
            isFetchingEquipmentRef.current = true;
            lastEquipmentFetchKeyRef.current = fetchKey;
            equipmentInitialLoadRef.current = true;

            try {
              console.log('🔄 Fetching equipment list from equipment API for Spares...', fetchKey);
              const equipmentList = await equipmentAPI.getEquipment(selectedCompany);

              if (equipmentList && Array.isArray(equipmentList) && equipmentList.length > 0) {
                setMachines(equipmentList);
                console.log('✅ Loaded equipment from equipment API:', equipmentList.length, 'machines');
              } else {
                console.warn('⚠️ No equipment data in equipment API response');
                setMachines([]);
              }
            } catch (error) {
              console.error('❌ Failed to fetch equipment list from equipment API:', error);
              setMachines([]);
            } finally {
              isFetchingEquipmentRef.current = false;
            }
          } else {
            if (isFetchingEquipmentRef.current) {
              console.log('⏸️ Equipment fetch already in progress, skipping duplicate request');
            } else if (fetchKey === lastEquipmentFetchKeyRef.current) {
              console.log('⏸️ Equipment already fetched for Spares, skipping');
            }
          }
        } else if (activeTab === 'Fuel' || activeTab === 'Maintenance' || activeTab === 'Predictive Maintenance') {
          // Use dedicated equipment API instead of analytics API to avoid duplicate calls
          // For Productivity, we now use availableMachines from context (populated by Productivity component)
          const fetchKey = `equip-${selectedCompany}`;

          // Fetch equipment on initial load OR when company changes
          const isInitialLoad = !equipmentInitialLoadRef.current && selectedCompany;
          const companyChanged = fetchKey !== lastEquipmentFetchKeyRef.current && selectedCompany;

          // Only fetch if:
          // 1. Not currently fetching (prevents concurrent calls)
          // 2. It's initial load OR company changed
          if (!isFetchingEquipmentRef.current && (isInitialLoad || companyChanged) && fetchKey !== lastEquipmentFetchKeyRef.current) {
            isFetchingEquipmentRef.current = true;
            lastEquipmentFetchKeyRef.current = fetchKey;

            // Mark initial load as done
            equipmentInitialLoadRef.current = true;

            try {
              console.log('🔄 Fetching equipment list from dedicated equipment API...');
              const equipmentData = await equipmentAPI.getEquipment(selectedCompany);

              if (equipmentData && Array.isArray(equipmentData) && equipmentData.length > 0) {
                setMachines(equipmentData);
                console.log('✅ Loaded equipment from equipment API:', equipmentData.length, 'machines');
              } else {
                console.warn('⚠️ No equipment data from equipment API');
                setMachines([]);
              }
            } catch (error) {
              console.error('❌ Failed to fetch equipment list:', error);
              setMachines([]);
            } finally {
              isFetchingEquipmentRef.current = false;
            }
          } else {
            if (isFetchingEquipmentRef.current) {
              console.log('⏸️ Equipment fetch already in progress, skipping duplicate request');
            } else if (fetchKey === lastEquipmentFetchKeyRef.current) {
              console.log('⏸️ Equipment already fetched for this company, skipping');
            }
          }
        } else if (activeTab !== 'Spares') {
          // Clear machines if not on a relevant tab (but keep for Spares)
          setMachines([]);
          // Don't reset cache - keep it so switching back doesn't refetch unnecessarily
        }

        // Set up shifts - common shift numbers (1, 2, 3)
        const commonShifts = [
          { shift_no: '1', shiftName: 'Shift 1' },
          { shift_no: '2', shiftName: 'Shift 2' },
          { shift_no: '3', shiftName: 'Shift 3' }
        ];
        setShifts(commonShifts);
        // Don't auto-select shift - user must choose

        console.log('📍 Locations loaded:', userLocations.length);
        console.log('⏰ Shifts set to common shifts (1, 2, 3)');
      } catch (error) {
        console.error('❌ Failed to fetch filter options:', error);
      }
    };

    fetchFilterOptions();
  }, [selectedCompany, dateRange?.from?.getTime(), dateRange?.to?.getTime(), selectedLocation, activeTab]);

  return (
    <div className="bg-white border-b border-gray-200 px-6 py-2 flex-shrink-0">
      <div className="flex items-center gap-4 flex-wrap">
        <ConfigProvider
          theme={{
            token: {
              colorPrimary: '#1890ff',
              borderRadius: 4,
            },
            components: {
              DatePicker: {
                headerBg: '#1890ff',
                headerColor: '#ffffff',
                headerFontSize: 14,
              },
            },
          }}
        >
          <div className="flex items-center gap-2">
            <div className="flex items-center bg-white border border-gray-200 rounded-md px-2 h-[30px]">
              <span className="text-[10px] text-gray-400 font-medium mr-1 uppercase">From</span>
              <DatePicker
                value={dateRange?.from ? dayjs(dateRange.from) : null}
                onChange={(date) => {
                  if (date) {
                    setDateRange(prev => ({ ...prev, from: date.toDate() }));
                  } else {
                    setDateRange(prev => ({ ...prev, from: undefined }));
                  }
                }}
                format="DD-MM-YYYY"
                placeholder="Start Date"
                className="border-none shadow-none p-0 text-xs w-[90px] hover:bg-transparent focus:bg-transparent"
                allowClear={false}
                showWeek
              />
            </div>

            <div className="flex items-center bg-white border border-gray-200 rounded-md px-2 h-[30px]">
              <span className="text-[10px] text-gray-400 font-medium mr-1 uppercase">To</span>
              <DatePicker
                value={dateRange?.to ? dayjs(dateRange.to) : null}
                onChange={(date) => {
                  if (date) {
                    setDateRange(prev => ({ ...prev, to: date.toDate() }));
                  } else {
                    setDateRange(prev => ({ ...prev, to: undefined }));
                  }
                }}
                format="DD-MM-YYYY"
                placeholder="Till Now"
                className="border-none shadow-none p-0 text-xs w-[90px] hover:bg-transparent focus:bg-transparent"
                allowClear={false}
                showWeek
                minDate={dateRange?.from ? dayjs(dateRange.from) : null}
              />
            </div>

            {(dateRange?.from || dateRange?.to) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const today = new Date();
                  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
                  setDateRange({ from: thisMonthStart, to: today });
                }}
                className="w-[30px] h-[30px] flex items-center justify-center rounded-md border border-gray-200 bg-white text-gray-400 hover:text-red-500 hover:border-red-200 hover:bg-red-50 transition-all shadow-sm"
                title="Reset to default date range"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </ConfigProvider>

        <Select value={selectedCompany} onValueChange={setSelectedCompany} disabled={loading}>
          <SelectTrigger className={`w-[144px] h-[30px] font-inter text-xs ${error ? 'border-red-500' : ''}`}>
            <SelectValue placeholder={loading ? "Loading..." : error ? "Error loading" : "company name"}>
              {selectedCompany && companies.find(c => c.companyId.toString() === selectedCompany)?.companyName}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {error && (
              <div className="px-2 py-1 text-xs text-red-600 bg-red-50">
                ❌ API Error: {error}
              </div>
            )}

            {companies.length > 0 ? (
              companies.map((company) => (
                <SelectItem key={company.companyId} value={company.companyId.toString()}>
                  {company.companyName}
                </SelectItem>
              ))
            ) : (
              !loading && !error && <SelectItem value="no-data" disabled>No companies available</SelectItem>
            )}
          </SelectContent>
        </Select>

        <Select
          value={selectedLocation}
          onValueChange={(value) => {
            setSelectedLocation(value);
            const location = locations.find(loc => loc.locationId.toString() === value);
            setSelectedLocationName(location?.locationName || '');
            // Clear machine selection when location changes - user must select machine for new location
            setSelectedMachine('');
            setSelectedMachineName('');
          }}
          disabled={locations.length === 0}
        >
          <SelectTrigger className={`${activeTab === 'Productivity' ? 'w-[120px]' : 'w-[144px]'} h-[30px] font-inter text-xs`}>
            <SelectValue placeholder={locations.length === 0 ? "No locations" : "Location"} />
          </SelectTrigger>
          <SelectContent>
            {locations.length > 0 ? (
              locations.map((location) => (
                <SelectItem key={location.locationId} value={location.locationId.toString()}>
                  {location.locationName}
                </SelectItem>
              ))
            ) : (
              <div className="px-2 py-1.5 text-xs text-gray-500">No locations available</div>
            )}
          </SelectContent>
        </Select>

        <Select
          value={selectedMachine}
          onValueChange={(value) => {
            setSelectedMachine(value);
            if (value === 'all') {
              setSelectedMachineName('All Machines');
              return;
            }
            const displayMachines = (activeTab === 'Productivity' || activeTab === 'Fuel' || activeTab === 'Maintenance' || activeTab === 'Predictive Maintenance' || activeTab === 'Spares') ? availableMachines : machines;
            const machine = displayMachines.find(mach => mach.machineId.toString() === value);
            setSelectedMachineName(machine?.machineName || '');
          }}
        // disabled={machines.length === 0}
        >
          <SelectTrigger className={`${activeTab === 'Productivity' ? 'w-[120px]' : 'w-[144px]'} h-[30px] font-inter text-xs`}>
            <SelectValue placeholder={machines.length === 0 ? "Machines" : " Machine"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Machines</SelectItem>
            {(() => {
              // For Productivity, Fuel, Maintenance, and Predictive Maintenance, use machines provided by the analytics API via context
              // For other tabs (Spares), use machines fetched by FilterBar
              const displayMachines = (activeTab === 'Productivity' || activeTab === 'Fuel' || activeTab === 'Maintenance' || activeTab === 'Predictive Maintenance' || activeTab === 'Spares') ? availableMachines : machines;

              if (displayMachines && displayMachines.length > 0) {
                return displayMachines.map((machine) => (
                  <SelectItem key={machine.machineId} value={machine.machineId.toString()}>
                    {machine.machineName}
                  </SelectItem>
                ));
              }
              return <div className="px-2 py-1.5 text-xs text-gray-500">No machines available</div>;
            })()}
          </SelectContent>
        </Select>

        {activeTab === 'Productivity' && (
          <>
            <Select
              value={selectedEquipmentType}
              onValueChange={setSelectedEquipmentType}
            >
              <SelectTrigger className="w-[120px] h-[30px] font-inter text-xs">
                <SelectValue placeholder="Equipment Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Equipment</SelectItem>
                <SelectItem value="2">Vehicle</SelectItem>
              </SelectContent>
            </Select>

          </>
        )}

        {activeTab !== 'Predictive Maintenance' && (
          <Select
            value={selectedShift}
            onValueChange={setSelectedShift}
            disabled={shifts.length === 0}
          >
            <SelectTrigger className={`${activeTab === 'Productivity' ? 'w-[120px]' : 'w-[144px]'} h-[30px] font-inter text-xs`}>
              <SelectValue placeholder={shifts.length === 0 ? "No shifts" : "Select Shift"} />
            </SelectTrigger>
            <SelectContent>
              {shifts.length > 0 ? (
                shifts.map((shift) => {
                  const shiftValue = shift.shift_no || shift.shiftId || '';
                  return (
                    <SelectItem key={shiftValue} value={shiftValue.toString()}>
                      {shift.shiftName}
                    </SelectItem>
                  );
                })
              ) : (
                <div className="px-2 py-1.5 text-xs text-gray-500">No shifts available</div>
              )}
            </SelectContent>
          </Select>
        )}

        <Button
          variant="outline"
          className="w-[30px] h-[30px] p-0 flex items-center justify-center transition-colors"
          style={{ backgroundColor: '#0d6efd', borderColor: '#0d6efd' }}
          aria-label="Apply filters"
          onClick={() => {
            console.log('🔵 Arrow button clicked - triggering API call');
            triggerManualFetch();
          }}
        >
          <ChevronRight className="h-4 w-4" style={{ color: '#ffffff' }} />
        </Button>
      </div>
    </div>
  );
};

export default FilterBar;
